import { useQuery } from "@tanstack/react-query";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API, formatPrice } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Reveal from "@/components/Reveal";
import { useCart } from "@/context/CartContext";
import { FileText, Layers, Zap, Users, BookOpen, ShoppingCart, Check } from "lucide-react";

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { addItem, has } = useCart();
  const { data: p, isLoading } = useQuery({ queryKey: ["product", slug], queryFn: async () => (await api.get(`/products/${slug}`)).data });

  if (isLoading) return <div className="min-h-[50vh] flex items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-terracotta border-t-transparent animate-spin" /></div>;
  if (!p) return <div className="text-center py-24 text-ink-muted">Produit introuvable.</div>;

  const buyNow = () => { addItem(p); nav("/panier"); };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
      <div className="grid lg:grid-cols-2 gap-12">
        <Reveal>
          <div className="bg-white border border-line rounded-lg p-8 sticky top-24">
            <img src={p.cover_url} alt={p.title} className="w-full max-w-sm mx-auto book-shadow rounded-sm" data-testid="product-cover" />
          </div>
        </Reveal>
        <div>
          <p className="font-overline text-terracotta mb-2">{p.category}</p>
          <h1 className="font-heading text-3xl sm:text-4xl text-ink tracking-tight" data-testid="product-title">{p.title}</h1>
          {p.subtitle && <p className="text-lg text-ink-muted mt-2 font-heading italic">{p.subtitle}</p>}
          <p className="mt-5 text-ink-muted leading-relaxed">{p.description}</p>
          <div className="flex items-baseline gap-3 mt-6">
            <span className="text-3xl font-semibold text-ink" data-testid="product-price">{formatPrice(p.price)}</span>
            {p.compare_price && <span className="text-lg text-ink-muted line-through">{formatPrice(p.compare_price)}</span>}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <Button size="lg" onClick={buyNow} className="bg-terracotta hover:bg-terracotta-dark text-white rounded-md px-8" data-testid="buy-now">
              <ShoppingCart className="mr-2 h-4 w-4" /> Acheter maintenant
            </Button>
            <Button size="lg" variant="outline" onClick={() => addItem(p)} disabled={has(p.id)} className="border-line text-ink rounded-md" data-testid="add-to-cart">
              {has(p.id) ? "Dans le panier" : "Ajouter au panier"}
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8">
            {[{ i: FileText, l: "Format", v: "PDF" }, { i: BookOpen, l: "Pages", v: p.pages }, { i: Zap, l: "Accès", v: "Immédiat" }, { i: Layers, l: "Type", v: "Numérique" }].map((x) => (
              <div key={x.l} className="bg-cream border border-line rounded-md p-3 text-center">
                <x.i className="h-4 w-4 text-terracotta mx-auto mb-1" />
                <p className="text-[11px] font-overline text-ink-muted">{x.l}</p>
                <p className="text-sm font-semibold text-ink">{x.v}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CE QUE VOUS ALLEZ DÉCOUVRIR */}
      <Section title="Ce que vous allez découvrir" icon={BookOpen}>
        <p className="text-ink-muted leading-relaxed max-w-3xl">{p.discover}</p>
      </Section>

      {/* POUR QUI */}
      <Section title="Pour qui ?" icon={Users}>
        <p className="text-ink-muted leading-relaxed max-w-3xl">{p.audience}</p>
      </Section>

      {/* CONTENU / SOMMAIRE */}
      {p.toc?.length > 0 && (
        <Section title="Sommaire — Contenu du livre" icon={Layers}>
          <div className="grid sm:grid-cols-2 gap-6">
            {p.toc.map((part, i) => (
              <div key={i} className="bg-cream border border-line rounded-lg p-5">
                <p className="font-heading text-lg text-ink mb-3">{part.part}</p>
                <ul className="space-y-1.5">
                  {part.items.map((it, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-ink-muted"><Check className="h-4 w-4 text-sage mt-0.5 shrink-0" />{it}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* APERÇU */}
      <Section title="Aperçu" icon={FileText}>
        <p className="text-ink-muted mb-4 text-sm">Consultez quelques pages avant l'achat. L'aperçu est limité et ne permet pas de télécharger le livre complet.</p>
        <div className="border border-line rounded-lg overflow-hidden bg-cream">
          <iframe title="Aperçu" src={`${API}/products/${p.slug}/preview#toolbar=0`} className="w-full h-[600px]" data-testid="preview-frame" />
        </div>
      </Section>

      <div className="mt-14 bg-ink text-sand rounded-xl p-8 text-center">
        <h3 className="font-heading text-2xl">Prêt à commencer votre lecture ?</h3>
        <p className="text-sand/70 mt-2 text-sm">Accès immédiat après paiement, sur tous vos appareils.</p>
        <Button size="lg" onClick={buyNow} className="mt-6 bg-terracotta hover:bg-terracotta-dark text-white rounded-md px-10" data-testid="buy-now-bottom">Acheter maintenant — {formatPrice(p.price)}</Button>
        <p className="text-sand/60 text-xs mt-4">Ou <Link to="/pack" className="underline">obtenez les 3 eBooks dans le pack</Link>.</p>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <Reveal className="mt-14">
      <div className="flex items-center gap-3 mb-5">
        <div className="h-9 w-9 rounded-full bg-cream border border-line flex items-center justify-center"><Icon className="h-4 w-4 text-terracotta" /></div>
        <h2 className="font-heading text-2xl text-ink tracking-tight">{title}</h2>
      </div>
      {children}
    </Reveal>
  );
}
